import { z } from 'zod';

type SignUpSchemaProps = {
  required: string;
  invalidEmail: string;
  invalidPhoneNumber: string;
};

type AuthSchemaProps = {
  required: string;
  invalidEmail: string;
};

// Login schema - no password complexity validation (just check if provided)
export const getLoginSchema = ({ required, invalidEmail }: AuthSchemaProps) =>
  z.object({
    email: z.string().min(1, { message: required }).email({ message: invalidEmail }),
    password: z.string().min(1, { message: required }),
  });

// Auth schema for password creation (signup, reset) - with complexity validation
export const getAuthSchema = ({ required, invalidEmail }: AuthSchemaProps) =>
  z.object({
    email: z.string().min(1, { message: required }).email({ message: invalidEmail }),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters long' })
      .regex(/[A-Z]/, { message: 'Password must include an uppercase letter' })
      .regex(/[a-z]/, { message: 'Password must include a lowercase letter' })
      .regex(/\d/, { message: 'Password must include a numeric character' })
      .regex(/[!@#$%^&*(),.?":{}|<>]/, { message: 'Password must include a special character' })
      .max(4096, { message: 'Password must be less than 100 characters' }),
  });

export const getSignUpSchema = (messages: SignUpSchemaProps) =>
  getAuthSchema(messages)
    .extend({
      firstName: z.string().min(1, { message: messages.required }),
      lastName: z.string().min(1, { message: messages.required }),
      phoneNumber: z
        .string()
        .optional()
        .refine((val) => !val || /^\+?[1-9]\d{1,14}$/.test(val), {
          message: messages.invalidPhoneNumber,
        }),
      phoneNumberCode: z.string().optional().default('+1'),
      confirmEmail: z.string().min(1, { message: messages.required }),
    })
    .refine((data) => data.email === data.confirmEmail, {
      message: 'Emails must match',
      path: ['confirmEmail'],
    });

export type LoginSchema = z.infer<ReturnType<typeof getLoginSchema>>;
export type AuthSchema = z.infer<ReturnType<typeof getAuthSchema>>;
export type SignUpSchema = z.infer<ReturnType<typeof getSignUpSchema>>;
